// The curated Model Book's drawing toolbar (curators only), mounted on
// `[data-model-book-editor]` in model_book/show.html.slim
// (docs/features/CURATED_MODEL_BOOK.md § Drawing).
//
// It owns no chart: it finds the `lightweight-chart` controller on the
// closest ancestor and binds on its `lightweight-chart:marks-loaded` event
// (re-binding if the chart is rebuilt). All drawing goes through that
// controller's marks API — addMark / removeMark — and its layer's
// setPreview / setSelected / hitTest; the only network is the JSON contract
// documented at the mount point (POST / PATCH label+note / DELETE).
//
// Placing: a kind tool subscribes to chart clicks (pattern from TradingView's
// plugin-examples rectangle-drawing-tool / trend-line). One-point kinds commit
// on the click; two-point kinds anchor, preview on crosshair move, and commit
// on the second click. Geometry is never edited: delete and redraw.
//
// CSP: every control is a server-rendered `data-action`; no inline handlers,
// no innerHTML from data.

import { Controller } from "@hotwired/stimulus"
import { swatchColor } from "../utils/chart_mark_styles"
import { readTheme } from "../utils/vcp_markup_overlay"
import { pointFor, placementStep, previewMark } from "../utils/chart_marks_editor"

const SELECT = "select"
// A one-point kind commits on its click; the library may still report the
// second half of that gesture as a dblclick after the POST resolved, which
// would select the new mark and hide its label form. Dblclicks this soon
// after a one-point commit are ignored.
const DBLCLICK_AFTER_COMMIT_MS = 600

export default class extends Controller {
  static targets = [
    "tool", "swatch", "deleteButton", "hint", "error",
    "popover", "form", "labelInput", "noteInput", "view", "viewAuthor", "viewLabel", "viewNote", "ownerActions"
  ]

  connect() {
    this.kinds = this.parseKinds()
    this.marksUrl = this.element.dataset.marksUrl
    this.tool = SELECT
    this.anchor = null
    this.selected = null
    this.editingId = null
    this.busy = false
    this.boundChart = null
    this.lastFailureStatus = null
    this.onePointCommittedAt = null

    this.onClick = param => this.chartClicked(param)
    this.onDblClick = param => this.chartDoubleClicked(param)
    this.onMove = param => this.crosshairMoved(param)
    this.onMarksLoaded = event => this.marksLoaded(event)
    this.onVisibilityChanged = () => this.visibilityChanged()
    this.onKeydown = event => this.keydown(event)
    this.onThemeChanged = () => this.paintSwatches()

    this.chartElement = this.element.closest("[data-controller~='lightweight-chart']")
    this.chartElement?.addEventListener("lightweight-chart:marks-loaded", this.onMarksLoaded)
    this.chartElement?.addEventListener("lightweight-chart:marks-visibility-changed", this.onVisibilityChanged)
    document.addEventListener("keydown", this.onKeydown)
    document.addEventListener("theme:changed", this.onThemeChanged)
    this.paintSwatches()
    this.renderTools()
    // The chart may have loaded before this controller connected.
    if (this.chartController()?.markOverlays) {
      this.bindChart()
      this.syncCount()
    }
  }

  disconnect() {
    this.unbindChart()
    this.chartElement?.removeEventListener("lightweight-chart:marks-loaded", this.onMarksLoaded)
    this.chartElement?.removeEventListener("lightweight-chart:marks-visibility-changed", this.onVisibilityChanged)
    document.removeEventListener("keydown", this.onKeydown)
    document.removeEventListener("theme:changed", this.onThemeChanged)
  }

  // ---- binding -------------------------------------------------------------

  chartController() {
    if (!this.chartElement) return null
    return this.application.getControllerForElementAndIdentifier(this.chartElement, "lightweight-chart")
  }

  layer() {
    return this.chartController()?.markOverlays || null
  }

  marksLoaded(event) {
    this.bindChart()
    this.syncCount()
  }

  // The author filter changed: a selection it hid is dropped, so Delete
  // can't remove a mark the curator can no longer see.
  visibilityChanged() {
    if (!this.selected) return
    const layer = this.layer()
    if (layer && typeof layer.isMarkVisible === "function" && layer.isMarkVisible(this.selected.id)) return
    this.select(null)
    this.hidePopover()
  }

  bindChart() {
    const chart = this.chartController()?.chart
    if (!chart || chart === this.boundChart) return
    this.unbindChart()
    chart.subscribeClick(this.onClick)
    // lightweight-charts reports the second of two clicks within 500ms as a
    // double-click (and, if they were >5px apart, as nothing at all). A
    // same-spot double-click is a real placement — a full-width level — so
    // it is treated as the second click (chartDoubleClicked).
    chart.subscribeDblClick(this.onDblClick)
    chart.subscribeCrosshairMove(this.onMove)
    this.boundChart = chart
    this.select(null)
    this.cancelPlacement()
  }

  unbindChart() {
    if (!this.boundChart) return
    try { this.boundChart.unsubscribeClick(this.onClick) } catch { /* chart already removed */ }
    try { this.boundChart.unsubscribeDblClick(this.onDblClick) } catch { /* chart already removed */ }
    try { this.boundChart.unsubscribeCrosshairMove(this.onMove) } catch { /* chart already removed */ }
    this.boundChart = null
  }

  // ---- toolbar -------------------------------------------------------------

  // Allowed while a create is in flight: create() only resets to Select if
  // the tool is still the one that placed the mark.
  pickTool(event) {
    const tool = event.currentTarget.dataset.tool
    this.setTool(this.kinds[tool] ? tool : SELECT)
  }

  setTool(tool) {
    this.tool = tool
    this.anchor = null
    this.layer()?.setPreview(null)
    this.clearError()
    if (tool !== SELECT) {
      this.select(null)
      this.hidePopover()
    }
    this.renderTools()
  }

  cancelPlacement() {
    this.setTool(SELECT)
  }

  renderTools() {
    this.toolTargets.forEach(button => {
      const active = button.dataset.tool === this.tool
      button.setAttribute("aria-pressed", active ? "true" : "false")
      button.classList.toggle("active", active)
    })
    const drawing = this.tool !== SELECT
    this.chartController()?.chartTarget?.classList.toggle("chart-marks-drawing", drawing)
    if (this.hasHintTarget) {
      const spec = this.kinds[this.tool]
      this.hintTarget.textContent = !drawing
        ? "Click a mark to select it."
        : this.anchor
          ? "Click the second point · Esc cancels"
          : `Click a bar to place ${spec.label.toLowerCase()}${spec.max_points > 1 ? " (first point)" : ""} · Esc cancels`
    }
    if (this.hasDeleteButtonTarget) this.deleteButtonTarget.disabled = !(this.selected?.own) || this.busy
  }

  paintSwatches() {
    const { dark } = readTheme()
    this.swatchTargets.forEach(swatch => {
      const color = swatchColor(swatch.dataset.kind, dark)
      if (color) swatch.style.backgroundColor = color
    })
  }

  // ---- chart events --------------------------------------------------------

  onMainPane(param) {
    return !!param?.point && (param.paneIndex === undefined || param.paneIndex === 0)
  }

  // The data-space point a chart event lands on for the active kind, or null.
  pointFromEvent(param) {
    const chart = this.chartController()
    if (!chart?.series || !this.onMainPane(param) || param.time === undefined) return null
    const series = chart.series
    const bar = param.seriesData?.get?.(series) || null
    return pointFor(this.tool, this.kinds[this.tool], {
      time: param.time,
      price: series.coordinateToPrice(param.point.y),
      bar,
      y: param.point.y,
      priceToY: price => series.priceToCoordinate(price)
    })
  }

  crosshairMoved(param) {
    if (this.tool === SELECT || this.busy) return
    const layer = this.layer()
    if (!layer) return
    const point = this.inVolumeStrip(param) ? null : this.pointFromEvent(param)
    layer.setPreview(point ? previewMark(this.tool, this.kinds[this.tool], this.anchor, point) : null)
  }

  // A price kind clicked in the volume histogram at the bottom of pane 0
  // would store a meaningless price; the chart controller knows the strip.
  inVolumeStrip(param) {
    const spec = this.kinds[this.tool]
    if (!spec || spec.pane === "volume" || !param?.point) return false
    const chart = this.chartController()
    return typeof chart?.inVolumeStrip === "function" && chart.inVolumeStrip(param.point.y)
  }

  chartDoubleClicked(param) {
    if (this.onePointCommittedAt !== null && Date.now() - this.onePointCommittedAt < DBLCLICK_AFTER_COMMIT_MS) return
    this.chartClicked(param)
  }

  chartClicked(param) {
    if (this.busy) return
    if (this.tool === SELECT) {
      this.selectAt(param)
      return
    }
    if (this.inVolumeStrip(param)) {
      this.showError("Click in the price area")
      return
    }
    const point = this.pointFromEvent(param)
    if (!point) {
      this.showError("Click on a bar, inside the price range.")
      return
    }
    this.clearError()
    const step = placementStep(this.tool, this.kinds[this.tool], this.anchor, point)
    this.anchor = step.anchor
    if (step.reason) this.showError(step.reason)
    if (step.commit) {
      this.layer()?.setPreview(null)
      this.create(this.tool, step.commit)
    }
    this.renderTools()
  }

  selectAt(param) {
    const layer = this.layer()
    if (!layer || !this.onMainPane(param)) return
    const mark = layer.hitTest(param.point.x, param.point.y)
    this.select(mark)
    if (mark) this.showView(mark)
    else this.hidePopover()
  }

  select(mark) {
    this.selected = mark || null
    this.layer()?.setSelected(this.selected ? this.selected.id : null)
    this.renderTools()
  }

  keydown(event) {
    const typing = event.target.closest?.("input, textarea, select, [contenteditable='true']")
    if (event.key === "Escape") {
      if (typing) event.target.blur()
      this.hidePopover()
      this.select(null)
      this.cancelPlacement()
    } else if ((event.key === "Delete" || event.key === "Backspace") && !typing && this.selected?.own) {
      event.preventDefault()
      this.deleteSelected()
    }
  }

  // ---- network -------------------------------------------------------------

  async create(kind, points) {
    const response = await this.request("POST", this.marksUrl, { chart_mark: { kind, points, label: "", note: "" } })
    if (!response) return
    const mark = response.mark
    this.chartController()?.addMark(mark)
    this.syncCount()
    if (points.length === 1) this.onePointCommittedAt = Date.now()
    // The curator may have picked another tool while the POST was in
    // flight; leave that tool (and its cleared selection) alone.
    if (this.tool !== kind) return
    this.setTool(SELECT)
    this.select(mark)
    this.showForm(mark)
  }

  async save(event) {
    event?.preventDefault()
    const id = this.editingId
    if (!id) return this.hidePopover()
    const response = await this.request("PATCH", `${this.marksUrl}/${id}`, {
      chart_mark: { label: this.labelInputTarget.value, note: this.noteInputTarget.value }
    })
    if (!response) return
    this.chartController()?.addMark(response.mark)
    this.select(response.mark)
    this.hidePopover()
  }

  skip(event) {
    event?.preventDefault()
    this.hidePopover()
  }

  edit(event) {
    event?.preventDefault()
    if (this.selected?.own) this.showForm(this.selected)
  }

  async deleteSelected(event) {
    event?.preventDefault()
    const mark = this.selected
    if (!mark?.own) return
    const response = await this.request("DELETE", `${this.marksUrl}/${mark.id}`)
    // A 404 means it is already gone server-side: drop it from the chart too.
    const alreadyGone = !response && this.lastFailureStatus === 404
    if (!response && !alreadyGone) return
    this.chartController()?.removeMark(mark.id)
    this.syncCount()
    this.select(null)
    this.hidePopover()
    if (alreadyGone) this.showError("That mark was already deleted")
  }

  // JSON request; returns the parsed body (or {} for a 204), or null after
  // showing the refusal in the toolbar. Never an alert(). A refusal's HTTP
  // status is left in lastFailureStatus (null after a network failure).
  async request(method, url, body = null) {
    this.busy = true
    this.lastFailureStatus = null
    this.clearError()
    this.renderTools()
    try {
      const headers = { Accept: "application/json", "X-CSRF-Token": this.csrfToken() }
      if (body) headers["Content-Type"] = "application/json"
      const response = await fetch(url, {
        method, headers, credentials: "same-origin", body: body ? JSON.stringify(body) : undefined
      })
      if (response.status === 204) return {}
      const json = await response.json().catch(() => ({}))
      if (response.ok) return json
      this.lastFailureStatus = response.status
      this.showError(this.errorText(json, response.status))
      return null
    } catch {
      this.showError("Could not reach the server — try again.")
      return null
    } finally {
      this.busy = false
      this.renderTools()
    }
  }

  errorText(json, status) {
    if (Array.isArray(json.errors) && json.errors.length > 0) return json.errors.join("; ")
    if (json.error) return String(json.error)
    return status === 403 ? "You can't change that mark." : `Request failed (${status}).`
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }

  // ---- popover -------------------------------------------------------------

  showForm(mark) {
    if (!this.hasPopoverTarget) return
    this.editingId = mark.id
    this.labelInputTarget.value = mark.label || ""
    this.noteInputTarget.value = mark.note || ""
    this.viewTarget.hidden = true
    this.formTarget.hidden = false
    this.popoverTarget.hidden = false
    this.labelInputTarget.focus()
  }

  showView(mark) {
    if (!this.hasPopoverTarget) return
    this.editingId = null
    this.viewAuthorTarget.textContent = mark.author?.alias || ""
    this.viewLabelTarget.textContent = mark.label || (this.kinds[mark.kind]?.label ?? mark.kind)
    this.viewNoteTarget.textContent = mark.note || ""
    this.viewNoteTarget.hidden = !mark.note
    this.ownerActionsTarget.hidden = !mark.own
    this.formTarget.hidden = true
    this.viewTarget.hidden = false
    this.popoverTarget.hidden = false
  }

  hidePopover() {
    this.editingId = null
    if (this.hasPopoverTarget) this.popoverTarget.hidden = true
  }

  // ---- status --------------------------------------------------------------

  showError(message) {
    if (this.hasErrorTarget) this.errorTarget.textContent = message
  }

  clearError() {
    if (this.hasErrorTarget) this.errorTarget.textContent = ""
  }

  // How many marks the chart's layer holds, written to data-marks-count (the
  // system spec reads it).
  syncCount() {
    const marks = this.layer()?.marks
    this.element.dataset.marksCount = String(Array.isArray(marks) ? marks.length : 0)
  }

  parseKinds() {
    try { return JSON.parse(this.element.dataset.kinds || "{}") } catch { return {} }
  }
}
